import { Skeleton } from '@/components/Skeleton'
import { themeStil } from '@/design/theme'
import { zerlegeMitGlossar } from '@/features/translate/glossar'
import type { Verfuegbarkeit } from '@/features/translate/useSprachausgabe'
import type { Lauf } from '@/features/translate/useUebersetzung'
import type { Dialekt } from '@/lib/api'

interface Props {
  dialekt: Dialekt
  lauf: Lauf
  verfuegbarkeit: Verfuegbarkeit
  spricht: boolean
  onAnhoeren: () => void
}

export function ErgebnisKarte({ dialekt, lauf, verfuegbarkeit, spricht, onAnhoeren }: Props) {
  const teile = zerlegeMitGlossar(lauf.text, lauf.ergebnis?.glossar ?? [])
  const kannSprechen = verfuegbarkeit === 'bereit' && lauf.zustand === 'fertig'

  return (
    <article
      style={themeStil(dialekt.theme)}
      data-dialekt={dialekt.id}
      data-motiv={dialekt.theme.motif}
      className="bg-surface-container rounded-card border-outline animate-rise overflow-hidden border"
    >
      <div className="bg-surface flex items-baseline justify-between gap-3 px-5 py-3">
        <div>
          <h3 className="font-display text-lg leading-none">{dialekt.name}</h3>
          <p className="text-on-surface-muted mt-1 text-xs">{dialekt.region}</p>
        </div>
        <div className="text-on-surface-muted text-right text-xs">
          {lauf.quelle === 'cache' && <span>aus dem Cache</span>}
          {lauf.ergebnis && <span className="ml-2">{lauf.ergebnis.latenz_ms} ms</span>}
        </div>
      </div>
      <div className="motiv" aria-hidden="true" />

      <div className="p-5">
        {lauf.zustand === 'fehler' ? (
          <p role="alert" className="text-sm">
            {lauf.meldung}
          </p>
        ) : lauf.text.length === 0 ? (
          <Skeleton breiten={['88%', '62%']} />
        ) : (
          <p className="font-display text-xl leading-snug" aria-live="polite">
            {teile.map((teil, index) =>
              teil.eintrag ? (
                <abbr
                  key={index}
                  title={`${teil.eintrag.hoch}${teil.eintrag.hinweis ? ` — ${teil.eintrag.hinweis}` : ''}`}
                  className="decoration-accent cursor-help underline decoration-dotted decoration-2 underline-offset-[6px]"
                >
                  {teil.text}
                </abbr>
              ) : (
                <span key={index}>{teil.text}</span>
              ),
            )}
            {lauf.zustand === 'laeuft' && (
              <span
                aria-hidden="true"
                className="bg-accent animate-atmen ml-1 inline-block h-4 w-1.5"
              />
            )}
          </p>
        )}

        {lauf.ergebnis && (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={onAnhoeren}
              disabled={!kannSprechen}
              className="border-outline rounded-pill hover:border-primary flex items-center gap-2 border px-3 py-1.5 text-sm transition-colors disabled:opacity-40"
            >
              <span aria-hidden="true">{spricht ? '■' : '▶'}</span>
              {spricht ? 'Stopp' : 'Anhören'}
            </button>

            {lauf.ergebnis.konfidenz < 0.6 && (
              <span className="text-on-surface-muted text-xs">
                unsicher ({lauf.ergebnis.konfidenz.toFixed(2)})
              </span>
            )}
            {dialekt.status === 'beta' && (
              <span
                className="border-outline text-on-surface-muted rounded-pill border px-2 py-0.5 text-[0.65rem] tracking-wider uppercase"
                title="Noch nicht von Muttersprachlern gegengelesen"
              >
                Beta
              </span>
            )}
          </div>
        )}

        {lauf.ergebnis && lauf.ergebnis.glossar.length > 0 && (
          <dl className="border-outline mt-4 grid gap-x-4 gap-y-1 border-t pt-3 text-sm sm:grid-cols-[auto_1fr]">
            {lauf.ergebnis.glossar.map((eintrag) => (
              <div key={eintrag.wort} className="contents">
                <dt className="font-medium">{eintrag.wort}</dt>
                <dd className="text-on-surface-muted">
                  {eintrag.hoch}
                  {eintrag.hinweis && ` — ${eintrag.hinweis}`}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </article>
  )
}
